import {
  Button,
  Menu,
  MenuItem,
  Stack,
  type SxProps,
  type Theme,
  useTheme,
} from "@mui/material";
import { CircularLoader, StatusBadge } from "components";
import { useGetAttributes } from "controller";
import React from "react";
import { statusData as STATUSDATA } from "shared/data";

/** The two fields of a status attribute item this menu reads. */
interface StatusOption {
  mapping_id: string;
  status_name: string;
}

export const StatusDropdown = ({
  status,
  value,
  lookupValue,
  onSelect,
  serviceType = "ticket",
  isDisabled = false,
  isLoading = false,
  endIcon,
  badgeContainerStyles,
}: {
  serviceType?: "ticket" | "general" | "request" | "project" | "task";
  status: string;
  value: string;
  /**
   * Untruncated status name, when `value` was shortened for display. Forwarded
   * to `StatusBadge` for its lookup only — see `StatusBadge`'s `lookupValue`.
   */
  lookupValue?: string;
  onSelect?: (value: string) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  endIcon?: React.ReactNode;
  /**
   * Overrides for the badge inside the trigger, merged over its defaults.
   * Lets a tight surface (the dispatch hover card) size the chip to its label
   * instead of the default minimum width.
   */
  badgeContainerStyles?: SxProps<Theme>;
}) => {
  const theme = useTheme();
  const { data: statusData } = useGetAttributes({
    attributeName: "status",
    serviceType,
  });

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = async () => {
    setAnchorEl(null);
  };
  const handleUpdate = async (id: string) => {
    onSelect && onSelect(id);
    setAnchorEl(null);
  };
  const blockedStatus: string[] = [
    STATUSDATA.CANCELLED,
    // STATUSDATA.CLOSED,
    STATUSDATA.MERGED,
    STATUSDATA.REOPEN,
    STATUSDATA.NEW,
    // "Escalated" is never a manual choice — escalation only happens via the
    // dedicated Escalate flow (drawer → POST /escalate). Status can move OUT
    // of Escalated normally, but not INTO it from this dropdown.
    STATUSDATA.ESCALATED,
  ];
  return (
    <div>
      <Button
        disabled={isDisabled || isLoading}
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        sx={{
          px: 0,
          py: 0,
          "&:hover": {
            "& .end-icon": {
              display: "flex",
            },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{ position: "relative" }}
        >
          <StatusBadge
            id={status}
            value={value}
            lookupValue={lookupValue}
            containerStyles={{ minWidth: "110px", ...badgeContainerStyles }}
          />
          <Stack
            className="end-icon"
            sx={{
              display: isLoading ? "flex" : "none",
              position: "absolute",
              right: 0,
              justifyContent: "center",
              alignItems: "center",
              pr: "4px",
              color: theme.palette.lightBg.high,
            }}
          >
            {isLoading ? (
              <CircularLoader
                size={16}
                sx={{
                  "&.MuiCircularProgress-root": {
                    color: "rgba(0, 0, 0, 0.2)",
                  },
                }}
              />
            ) : (
              endIcon
            )}
          </Stack>
        </Stack>
      </Button>
      <Menu
        sx={{
          minWidth: "120px",
          maxHeight: "300px",
        }}
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {((statusData as StatusOption[] | undefined) ?? [])
          .filter((item) => !blockedStatus.includes(item.mapping_id))
          .map((status) => (
            <MenuItem
              key={status.mapping_id}
              onClick={() => handleUpdate(status.mapping_id)}
            >
              <StatusBadge id={status.mapping_id} value={status.status_name} />
            </MenuItem>
          ))}
      </Menu>
    </div>
  );
};
